import React, { useContext, useMemo } from "react";
import { ThemeContext } from "../../constant/theme";
import { UserPlus, RefreshCw, Shield, Check } from "lucide-react";

import useAdminAccountCreation from "./hooks/useAdminAccountCreation";
import ConfigurableButton from "../../components/ConfigurableButton";
import ReusableDropdown from "../../components/ReusableDropdown";
import {
    getInputStyle,
    getCardStyle,
    getHintStyle,
    gridStyle,
    labelStyle,
} from "./styles/adminAccountStyles";

const AdminAccountCreation = () => {
    const { theme } = useContext(ThemeContext);
    const [focusedField, setFocusedField] = React.useState(null);

    const {
        companies,
        loadingCompanies,
        saving,
        message,
        form,
        onChange,
        handleCreateAdmin,
    } = useAdminAccountCreation();

    const inputStyle = useMemo(() => getInputStyle(theme), [theme]);

    const getFieldStyle = (fieldName) => ({
        ...inputStyle,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: focusedField === fieldName
            ? theme === "dark"
                ? "0 0 0 3px rgba(46, 124, 186, 0.3), 0 8px 24px rgba(0, 0, 0, 0.3)"
                : "0 0 0 3px rgba(46, 124, 186, 0.2), 0 8px 16px rgba(46, 124, 186, 0.12)"
            : "0 2px 8px rgba(0, 0, 0, 0.06)",
        borderColor: focusedField === fieldName ? "#2e7cba" : inputStyle.borderColor,
    });

    return (
        <div style={{ minWidth: "70vw",minHeight: "110vh", padding: "10px 0" }}>
            <div>
                <div style={{
                    background: theme === "dark"
                        ? "linear-gradient(135deg, rgba(46, 124, 186, 0.2) 0%, rgba(30, 95, 143, 0.15) 100%)"
                        : "linear-gradient(135deg, rgba(46, 124, 186, 0.08) 0%, rgba(30, 95, 143, 0.05) 100%)",
                    borderRadius: "20px",
                    padding: "32px",
                    marginBottom: "40px",
                    color: theme === "dark" ? "#fff" : "#1f2937",
                    border: theme === "dark"
                        ? "1px solid rgba(46, 124, 186, 0.3)"
                        : "1px solid rgba(46, 124, 186, 0.15)",
                    boxShadow: theme === "dark"
                        ? "inset 0 2px 8px rgba(46, 124, 186, 0.1)"
                        : "inset 0 2px 8px rgba(46, 124, 186, 0.08)",
                }}>
                    <h2 style={{
                        color: theme === "dark" ? "#fff" : "#1e5f8f",
                        margin: "0 0 8px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        fontSize: "26px",
                        fontWeight: "800",
                    }}>
                        <Shield size={28} />
                        Admin Account Creation
                    </h2>
                    <p style={{
                        margin: 0,
                        fontSize: "14px",
                        opacity: theme === "dark" ? 0.85 : 0.75,
                        lineHeight: 1.6,
                    }}>
                        Create a new employee account and assign admin role immediately.
                    </p>
                </div>

                {message.text && (
                    <div style={{
                        marginBottom: "24px",
                        padding: "12px 16px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontSize: "14px",
                        fontWeight: "500",
                        background: message.type === "success"
                            ? "rgba(16, 185, 129, 0.12)"
                            : message.type === "error"
                            ? "rgba(239, 68, 68, 0.12)"
                            : "rgba(245, 158, 11, 0.12)",
                        color: message.type === "success"
                            ? "#059669"
                            : message.type === "error"
                            ? "#dc2626"
                            : "#d97706",
                        border: `1px solid ${
                            message.type === "success"
                                ? "rgba(16, 185, 129, 0.3)"
                                : message.type === "error"
                                ? "rgba(239, 68, 68, 0.3)"
                                : "rgba(245, 158, 11, 0.3)"
                        }`,
                    }}>
                        {message.type === "success" ? (
                            <Check size={18} />
                        ) : message.type === "error" ? (
                            <Shield size={18} />
                        ) : (
                            <Shield size={18} />
                        )}
                        <span>{message.text}</span>
                    </div>
                )}

                <form onSubmit={handleCreateAdmin}>
                    <div style={gridStyle}>
                        {[
                            { key: "sr", label: "Admin Name", type: "text", placeholder: "Full name", required: true },
                            { key: "email", label: "Email", type: "email", placeholder: "admin@company.com", required: true },
                            { key: "clientName", label: "Company", type: "select", required: true },
                            { key: "password", label: "Password", type: "password", placeholder: "Enter account password", required: true },
                            { key: "infinityCode", label: "Infinity Code", type: "text", placeholder: "Leave empty to auto-generate", required: false },
                            { key: "phoneNumber", label: "Phone", type: "text", placeholder: "e.g. 010xxxxxxx", required: false },
                            { key: "location", label: "Location", type: "text", placeholder: "Office/Branch", required: false },
                        ].map((field) => (
                            <div key={field.key}>
                                <label style={{ ...labelStyle, color: theme === "dark" ? "#fff" : "#1f2937", fontWeight: "600", fontSize: "14px" }}>
                                    {field.label}
                                    {!field.required && <span style={{ opacity: 0.7 }}> (optional)</span>}
                                    {field.required && <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>}
                                </label>
                                {field.type === "select" ? (
                                    <ReusableDropdown
                                        items={companies}
                                        value={form.clientName}
                                        onChange={(value) => onChange("clientName", value)}
                                        getItemKey={(item) => item.clientName}
                                        getItemLabel={(item) => item.clientName}
                                        placeholder="Select company"
                                        theme={theme}
                                        disabled={loadingCompanies}
                                        inputStyle={getFieldStyle("clientName")}
                                    />
                                ) : (
                                    <input
                                        type={field.type}
                                        value={form[field.key]}
                                        onChange={(e) => onChange(field.key, e.target.value)}
                                        style={getFieldStyle(field.key)}
                                        placeholder={field.placeholder}
                                        onFocus={() => setFocusedField(field.key)}
                                        onBlur={() => setFocusedField(null)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{
                        ...getHintStyle(theme),
                        background: theme === "dark"
                            ? "rgba(46, 124, 186, 0.1)"
                            : "rgba(46, 124, 186, 0.08)",
                        border: `1px solid ${theme === "dark" ? "rgba(46, 124, 186, 0.3)" : "rgba(46, 124, 186, 0.2)"}`,
                        color: theme === "dark" ? "rgba(255, 255, 255, 0.85)" : "#1e5f8f",
                        fontSize: "13px",
                        lineHeight: "1.6",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                    }}>
                        <span style={{ marginTop: "2px" }}>🔒</span>
                        <span>Password will be securely hashed with bcrypt before storing in the database.</span>
                    </div>

                    <ConfigurableButton
                        text={saving ? "Creating Admin..." : "Create Admin Account"}
                        bgColor="#2e7cba"
                        color="#ffffff"
                        width="100%"
                        height="48px"
                        borderRadius="12px"
                        onClick={handleCreateAdmin}
                        disabled={saving || loadingCompanies}
                        type="submit"
                        style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            boxShadow: saving || loadingCompanies
                                ? "none"
                                : theme === "dark"
                                ? "0 10px 25px rgba(46, 124, 186, 0.2)"
                                : "0 10px 25px rgba(46, 124, 186, 0.15)",
                            gap: "10px",
                        }}
                    >
                        {saving ? (
                            <RefreshCw size={18} style={{ animation: "spin 1s linear infinite" }} />
                        ) : (
                            <UserPlus size={18} />
                        )}
                        {saving ? "Creating Admin..." : "Create Admin Account"}
                    </ConfigurableButton>
                </form>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                input::-webkit-autofill,
                input:-webkit-autofill:hover,
                input:-webkit-autofill:focus {
                    -webkit-box-shadow: 0 0 0 30px ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#fff"} inset !important;
                    -webkit-text-fill-color: ${theme === "dark" ? "#fff" : "#111827"} !important;
                }
            `}</style>
        </div>
    );
};

export default AdminAccountCreation;